import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U2201';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
} as const;

export interface U2201InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
}

export interface U2201JobSchedule {
  id: string;
  version: number;
  scheduleCode: string;
  jobType: string;
  payload: Record<string, unknown>;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastJobId: string | null;
  maxAttempts: number;
}

/** Ligne search (champs selon filter.fields). */
export type U2201JobScheduleSearchRow = Partial<U2201JobSchedule> & {
  id: string;
  version: number;
};

/**
 * Formulaire : `payloadJson` string pour U2000InputMemo ;
 * payload objet conservé pour le save.
 */
export type U2201JobScheduleFormRow = Omit<U2201JobSchedule, 'payload'> & {
  payload: Record<string, unknown>;
  payloadJson: string;
};

export interface U2201JobScheduleUpdateBody {
  id: string;
  version: number;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  payload: Record<string, unknown>;
  maxAttempts: number;
}

export const U2201_ERROR_INVALID_CRON_TZ = 'BU220101';

export const defaultU2201InitLU: U2201InitLU = {
  launchSearch: true,
  hideSearch: false,
  nbRecordPerPage: 50,
  logicalUnitCode: 'U2201',
};

export function payloadToJsonString(payload: unknown): string {
  try {
    return JSON.stringify(payload ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

export function normalizeJobScheduleFromApi(row: U2201JobSchedule): U2201JobScheduleFormRow {
  return {
    ...row,
    payload: row.payload && typeof row.payload === 'object' ? row.payload : {},
    payloadJson: payloadToJsonString(row.payload),
    nextRunAt: row.nextRunAt ?? null,
    lastRunAt: row.lastRunAt ?? null,
    lastJobId: row.lastJobId ?? null,
    maxAttempts: row.maxAttempts ?? 1,
    enabled: Boolean(row.enabled),
  };
}

export function buildUpdateBody(row: U2201JobScheduleFormRow): U2201JobScheduleUpdateBody {
  let payload: Record<string, unknown> = row.payload ?? {};
  const raw = row.payloadJson?.trim() ?? '';
  if (raw !== '') {
    payload = JSON.parse(raw) as Record<string, unknown>;
  } else {
    payload = {};
  }
  const maxAttemptsRaw = Number(row.maxAttempts);
  const maxAttempts =
    Number.isFinite(maxAttemptsRaw) ? Math.trunc(maxAttemptsRaw) : 1;
  return {
    id: row.id,
    version: row.version,
    cronExpression: row.cronExpression.trim(),
    timezone: row.timezone.trim(),
    enabled: Boolean(row.enabled),
    payload,
    maxAttempts: maxAttempts < 1 ? 1 : maxAttempts,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doUpdateData = (
  axios: AxiosInstance,
  id: string | number,
  body: U2201JobScheduleUpdateBody
) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export function luHandleAppiError(
  t: TranslateFn,
  nameSpace: string,
  err: unknown,
  errorResult: U2000ApiErrorResult
): void {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U2201_ERROR_INVALID_CRON_TZ:
      errorResult.fieldErrors['cronExpression'] = t('errInvalidCronOrTimezone', { ns: nameSpace });
      errorResult.fieldErrors['timezone'] = t('errInvalidCronOrTimezone', { ns: nameSpace });
      break;
    default:
      break;
  }
}
